import { useState } from 'react';
import { HelpTip, PANEL_HELP, PanelHowTo, api, useToast } from './_barrel.js';


        function BusinessSynthesisPanel({ token, businessId }) {
            const [verdict, setVerdict] = useState(null);
            const [loading, setLoading] = useState(false);
            const [err, setErr] = useState(null);
            const [refreshing, setRefreshing] = useState(false);
            const [refreshMsg, setRefreshMsg] = useState(null);
            const toast = useToast();

            const generate = async () => {
                setLoading(true); setErr(null);
                try {
                    setVerdict(await api(`/api/v1/businesses/${businessId}/synthesis`, { method: 'POST' }, token));
                } catch (e) { setErr(e.message); } finally { setLoading(false); }
            };

            // One-click full refresh (media + exposure) then re-verdict — the
            // business counterpart to the subject assessment's refresh-and-rescore.
            const refreshAll = async () => {
                setRefreshing(true); setErr(null);
                setRefreshMsg('Media…');
                await Promise.allSettled([api(`/api/v1/businesses/${businessId}/collectors/google-news`, { method: 'POST' }, token)]);
                setRefreshMsg('Exposure…');
                await Promise.allSettled([api(`/api/v1/businesses/${businessId}/exposure/screen`, { method: 'POST' }, token)]);
                setRefreshMsg('Re-scoring…');
                try { setVerdict(await api(`/api/v1/businesses/${businessId}/synthesis`, { method: 'POST' }, token)); }
                catch (e) { setErr(e.message); }
                setRefreshing(false); setRefreshMsg(null);
                toast('Refreshed & re-scored from all sources');
            };

            const recColour = r => r === 'do_not_proceed' ? 'var(--red)' : r === 'caution' ? 'var(--amber, #f59e0b)' : 'var(--green)';

            return (
              <div className="panel mb-6">
                <div className="panel-header flex justify-between items-center">
                  <div>
                    <div className="font-display text-xl gold">Synthesis Verdict<HelpTip text={PANEL_HELP.synthesis_verdict} /></div>
                    <div className="text-xs text-muted font-mono mt-1">
                        {refreshing ? <span style={{color:'var(--cyan)'}}>{refreshMsg || 'Refreshing…'}</span> : 'Should you engage? Fuses corporate health, exposure, media & people'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="btn-ghost" onClick={refreshAll} disabled={refreshing || loading} title="Refresh media + exposure from every source, then regenerate the verdict against the current picture.">
                        {refreshing ? 'Refreshing…' : '🤖 Refresh & re-verdict'}
                    </button>
                    <button className="btn-gold" onClick={generate} disabled={loading || refreshing}>
                        {loading ? 'Analysing…' : (verdict ? '↻ Regenerate' : '⚖ Generate verdict')}
                    </button>
                  </div>
                </div>
                <PanelHowTo id="synthesis_verdict" />
                <div className="px-6 py-5">
                  {err && <div className="text-[var(--red)] text-sm mb-3">{err}</div>}
                  {!verdict ? (
                    <div className="text-sm text-muted">Generate a decision-grade recommendation — <b>Proceed / Proceed with caution / Do not proceed</b> — with the specific reasons and a pre-meeting checklist, from everything Veil holds on this company.</div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="font-display text-2xl" style={{ color: recColour(verdict.recommendation) }}>{verdict.recommendation_label}</div>
                        {verdict.engine === 'rules' && <span className="text-xs text-muted font-mono" title="No LLM key configured — rules engine used">rules</span>}
                      </div>
                      {verdict.headline && <div className="text-sm gold mb-3">{verdict.headline}</div>}
                      <div className="text-sm text-paper mb-4 leading-relaxed">{verdict.bottom_line}</div>
                      <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2">Why</div>
                      <ul className="mb-4">{(verdict.reasons || []).map((r, i) => <li key={i} className="text-sm mb-1">• {r}</li>)}</ul>
                      <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2">Verify before the meeting</div>
                      <ul>{(verdict.pre_meeting_checklist || []).map((r, i) => <li key={i} className="text-sm text-muted mb-1">☐ {r}</li>)}</ul>
                      <div className="text-xs text-dim font-mono mt-4">Draft recommendation for analyst review — sign it off below to put it on the record.</div>
                    </>
                  )}
                </div>
              </div>
            );
        }

export { BusinessSynthesisPanel };
